/**
 * DocumentReviewSuggestions
 *
 * Renders the result of the chat's `document_review` route, with the new
 * "auto-applied" UX:
 *
 *   1. As soon as backend emits `document_suggestions` (or per-section
 *      `review_section_complete`), each suggestion is found in the live PM
 *      doc and *immediately* applied as a track-change mark
 *      (suggestionDelete + suggestionInsert). The user sees them in the
 *      document right away, no manual Apply step.
 *   2. The chat panel becomes a control surface:
 *        - Accept / Reject per suggestion (commits or reverts the change in
 *          the document, removes the marks).
 *        - Click anywhere on a card → editor scrolls to that suggestion's
 *          mark and briefly outlines it.
 *        - "Accept all pending" button at the top.
 *   3. Suggestions whose search_text can't be located in the live document
 *      get the `not_found` status and are shown in muted form with a chip.
 *
 * Status model:
 *   - pending     (just arrived from server, not yet auto-applied)
 *   - applied     (track-change exists in document, awaiting user decision)
 *   - accepted    (user kept it, marks removed, replacement committed)
 *   - rejected    (user discarded it, marks removed, original kept)
 *   - not_found   (couldn't locate search_text in live document)
 */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  LinearProgress,
  Collapse,
  alpha,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RateReviewIcon from '@mui/icons-material/RateReview';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useDispatch, useSelector } from 'react-redux';
import EditorContext from '../../../../contexts/EditorContext';
import { updateReviewSuggestionStatus } from '../../../../redux/actions/contractAction';
import { findRange } from '../../DocumentEditor/utils/textRangeMatcher';

const SEVERITY_CONFIG = {
  critical: { label: 'Critical', color: 'error', icon: ErrorOutlineIcon },
  warning: { label: 'Warning', color: 'warning', icon: WarningAmberIcon },
  info: { label: 'Info', color: 'info', icon: InfoOutlinedIcon },
};

const severityRank = (s) => (s === 'critical' ? 0 : s === 'warning' ? 1 : 2);

function ReviewProgressBar({ progress }) {
  const theme = useTheme();
  if (!progress || !progress.totalSections) return null;
  const sections = Object.values(progress.sections || {});
  const completed = sections.filter((s) => s.status === 'complete').length;
  const pct = Math.round((completed / progress.totalSections) * 100);
  const inFlight = sections.find((s) => s.status === 'in_progress');
  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
          Reviewing {completed}/{progress.totalSections} sections
        </Typography>
        <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
          {pct}%
        </Typography>
      </Box>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 4, borderRadius: 2 }} />
      {inFlight && (
        <Typography
          variant="caption"
          sx={{ color: theme.palette.text.disabled, mt: 0.5, display: 'block', fontStyle: 'italic' }}
        >
          → {inFlight.title}
        </Typography>
      )}
    </Box>
  );
}

function SuggestionCard({ suggestion, onAccept, onReject, onScroll }) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const sevConfig = SEVERITY_CONFIG[suggestion.severity] || SEVERITY_CONFIG.info;
  const SevIcon = sevConfig.icon;
  const status = suggestion.status || 'pending';
  const isApplied = status === 'applied';
  const isAccepted = status === 'accepted';
  const isRejected = status === 'rejected';
  const isMissing = status === 'not_found';
  const isActionable = isApplied;
  // Card is clickable to scroll if the suggestion is in the document. We
  // don't require mark_id because the resolver falls back to text matching
  // (which survives page reload that renames temp ids to permanent ones).
  const canScroll = isApplied && !!suggestion.search_text;

  const borderColor = isAccepted
    ? theme.palette.success.main
    : isRejected
      ? theme.palette.text.disabled
      : isMissing
        ? theme.palette.warning.main
        : theme.palette[sevConfig.color]?.main || theme.palette.divider;

  return (
    <Box
      onClick={(e) => {
        // Don't intercept button clicks
        if (e.target.closest('button')) return;
        if (canScroll) onScroll(suggestion);
      }}
      sx={{
        borderLeft: `3px solid ${borderColor}`,
        borderRadius: '0 4px 4px 0',
        mb: 1,
        p: 1.25,
        bgcolor: isRejected
          ? alpha(theme.palette.action.disabledBackground, 0.3)
          : alpha(theme.palette.background.paper, 0.6),
        opacity: isRejected ? 0.55 : isMissing ? 0.85 : 1,
        cursor: canScroll ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        '&:hover': canScroll
          ? { bgcolor: alpha(theme.palette.action.hover, 0.5) }
          : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Chip
            icon={<SevIcon sx={{ fontSize: 14 }} />}
            label={sevConfig.label}
            size="small"
            color={sevConfig.color}
            variant="outlined"
            sx={{ height: 22, '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 } }}
          />
          {suggestion.section_title && (
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.disabled,
                fontSize: '0.68rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 180,
              }}
              title={suggestion.section_title}
            >
              · {suggestion.section_title}
            </Typography>
          )}
        </Box>
        {isAccepted && (
          <Chip icon={<CheckIcon sx={{ fontSize: 12 }} />} label="Accepted" size="small" color="success"
            sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 } }} />
        )}
        {isRejected && (
          <Chip label="Rejected" size="small"
            sx={{ height: 20, bgcolor: theme.palette.action.disabledBackground, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 } }} />
        )}
        {isMissing && (
          <Chip label="Not found in document" size="small" color="warning" variant="outlined"
            sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 } }} />
        )}
        {isApplied && (
          <Chip label="In document" size="small" variant="outlined"
            sx={{ height: 20, color: theme.palette.text.secondary, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.5 } }} />
        )}
      </Box>

      <Typography
        variant="body2"
        sx={{
          fontSize: '0.8rem',
          color: theme.palette.text.secondary,
          mb: 0.75,
          lineHeight: 1.45,
          textAlign: 'left',
        }}
      >
        {suggestion.explanation}
      </Typography>

      <Collapse in={expanded} timeout={150}>
        <Box
          sx={{
            bgcolor: alpha(theme.palette.common.black, 0.03),
            borderRadius: 1,
            p: 0.75,
            mb: 0.75,
            fontSize: '0.78rem',
            lineHeight: 1.5,
            textAlign: 'left',
          }}
        >
          <Box
            sx={{
              textDecoration: 'line-through',
              color: theme.palette.error.main,
              opacity: 0.7,
              mb: 0.5,
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxHeight: 220,
              overflowY: 'auto',
            }}
          >
            {suggestion.search_text}
          </Box>
          <Box
            sx={{
              color: theme.palette.success.dark,
              fontWeight: 500,
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              maxHeight: 240,
              overflowY: 'auto',
            }}
          >
            {suggestion.replacement_text}
          </Box>
        </Box>
      </Collapse>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.25, mb: 0.5 }}>
        <Button
          size="small"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          endIcon={expanded ? <ExpandLessIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
          sx={{
            height: 20, minWidth: 'auto', px: 0.75,
            fontSize: '0.65rem', textTransform: 'none',
            color: theme.palette.text.secondary,
          }}
        >
          {expanded ? 'Hide diff' : 'Show diff'}
        </Button>
      </Box>

      {isActionable && (
        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
          <Button
            variant="contained"
            size="small"
            onClick={(e) => { e.stopPropagation(); onAccept(suggestion); }}
            startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
            sx={{
              height: 26, fontSize: '0.72rem', textTransform: 'none',
              bgcolor: theme.palette.success.main,
              '&:hover': { bgcolor: theme.palette.success.dark },
              px: 1.5,
            }}
          >
            Accept
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={(e) => { e.stopPropagation(); onReject(suggestion); }}
            startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
            sx={{
              height: 26, fontSize: '0.72rem', textTransform: 'none', px: 1,
              borderColor: theme.palette.divider,
              color: theme.palette.text.secondary,
              '&:hover': {
                borderColor: theme.palette.text.secondary,
                bgcolor: alpha(theme.palette.action.hover, 0.5),
              },
            }}
          >
            Reject
          </Button>
        </Box>
      )}
    </Box>
  );
}

const DocumentReviewSuggestions = ({ message }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const editorRef = useContext(EditorContext);
  const contract = useSelector((state) => state.contract.contract);
  const currentUser = useSelector((state) => state.auth?.user?.user);
  const userRole = (contract?.collaborators || []).find((c) => c.id === currentUser?.id)?.role;
  const isLocked =
    ['PENDING_SIGNATURE', 'SIGNED'].includes(contract?.status) || userRole === 'reviewer';
  const [collapsed, setCollapsed] = useState(false);

  const suggestions = message.review_suggestions || [];
  const progress = message.review_progress;

  // Auto-apply each `pending` suggestion as soon as it lands in the message.
  // We track which suggestion_ids we've already auto-applied to avoid
  // re-applying on every render.
  const autoAppliedRef = useRef(new Set());
  useEffect(() => {
    if (isLocked) return;
    if (!editorRef?.current?.addAISuggestion) return;
    const editor = editorRef.current.getEditor();
    if (!editor) return;

    suggestions.forEach((s) => {
      if (s.status !== 'pending') return;
      if (autoAppliedRef.current.has(s.suggestion_id)) return;

      const range = findRange(editor.view.state.doc, s.search_text);
      if (!range) {
        autoAppliedRef.current.add(s.suggestion_id);
        dispatch(updateReviewSuggestionStatus(message.id, s.suggestion_id, 'not_found'));
        return;
      }

      const markId = editorRef.current.addAISuggestion({
        from: range.from,
        to: range.to,
        originalText: s.search_text,
        suggestedText: s.replacement_text,
      });
      autoAppliedRef.current.add(s.suggestion_id);
      if (markId) {
        dispatch(
          updateReviewSuggestionStatus(message.id, s.suggestion_id, 'applied', markId),
        );
      } else {
        dispatch(updateReviewSuggestionStatus(message.id, s.suggestion_id, 'not_found'));
      }
    });
  }, [suggestions, message.id, dispatch, editorRef, isLocked]);

  // Sort: severity first, then by document position if known (mark_id present
  // implies the suggestion is in the doc; we use the rendered DOM order).
  const sortedSuggestions = useMemo(() => {
    const editor = editorRef?.current?.getEditor?.();
    let domOrder = null;
    if (editor) {
      try {
        const dom = editor.view.dom;
        const els = Array.from(dom.querySelectorAll('[data-sid]'));
        domOrder = new Map();
        els.forEach((el, idx) => {
          const id = el.getAttribute('data-sid');
          if (!domOrder.has(id)) domOrder.set(id, idx);
        });
      } catch {
        domOrder = null;
      }
    }
    return [...suggestions].sort((a, b) => {
      const r = severityRank(a.severity) - severityRank(b.severity);
      if (r !== 0) return r;
      if (domOrder && a.mark_id && b.mark_id) {
        const ai = domOrder.get(String(a.mark_id));
        const bi = domOrder.get(String(b.mark_id));
        if (ai != null && bi != null) return ai - bi;
      }
      return (a.suggestion_id || '').localeCompare(b.suggestion_id || '');
    });
  }, [suggestions, editorRef]);

  const counts = useMemo(() => {
    const c = { critical: 0, warning: 0, info: 0, applied: 0, accepted: 0, rejected: 0 };
    suggestions.forEach((s) => {
      c[s.severity] = (c[s.severity] || 0) + 1;
      if (s.status === 'applied') c.applied += 1;
      if (s.status === 'accepted') c.accepted += 1;
      if (s.status === 'rejected') c.rejected += 1;
    });
    return c;
  }, [suggestions]);

  // Resolve the suggestion's current editor mark id. Falls back to a
  // text-content match against <del> elements when the stored mark_id is
  // stale (e.g., after a page reload that renamed temp ids to permanent).
  const resolveMarkId = (s) => {
    if (!editorRef?.current?.resolveSuggestionMarkId) return s.mark_id || null;
    return editorRef.current.resolveSuggestionMarkId(s.mark_id, s.search_text);
  };

  const acceptOne = (s) => {
    const sid = resolveMarkId(s);
    if (!sid) return;
    if (editorRef?.current?.acceptAISuggestion) {
      editorRef.current.acceptAISuggestion(sid);
    }
    dispatch(updateReviewSuggestionStatus(message.id, s.suggestion_id, 'accepted'));
  };

  const rejectOne = (s) => {
    const sid = resolveMarkId(s);
    if (sid && editorRef?.current?.rejectAISuggestion) {
      editorRef.current.rejectAISuggestion(sid);
    }
    dispatch(updateReviewSuggestionStatus(message.id, s.suggestion_id, 'rejected'));
  };

  const scrollToOne = (s) => {
    if (editorRef?.current?.scrollToSuggestion) {
      // Pass both the stored mark_id AND the search_text so the resolver
      // can fall back to text matching after a page reload.
      editorRef.current.scrollToSuggestion(s.mark_id, s.search_text);
    }
  };

  const acceptAll = () => {
    if (isLocked) return;
    suggestions
      .filter((s) => s.status === 'applied' && s.mark_id)
      .forEach(acceptOne);
  };

  const rejectAll = () => {
    if (isLocked) return;
    suggestions
      .filter((s) => s.status === 'applied' && s.mark_id)
      .forEach(rejectOne);
  };

  const reviewInProgress =
    progress &&
    progress.totalSections > 0 &&
    Object.values(progress.sections || {}).some((s) => s.status !== 'complete');

  if (suggestions.length === 0 && !reviewInProgress) return null;

  return (
    <Box
      sx={{
        mb: 1,
        p: 1.25,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <RateReviewIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
            Document review
          </Typography>
          {suggestions.length > 0 && (
            <Chip
              label={`${suggestions.length} suggestion${suggestions.length !== 1 ? 's' : ''}`}
              size="small"
              sx={{ height: 18, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.6 } }}
            />
          )}
        </Box>
        {suggestions.length > 0 && (
          <Button
            size="small"
            onClick={() => setCollapsed((v) => !v)}
            sx={{ height: 22, minWidth: 'auto', px: 0.5, fontSize: '0.65rem', textTransform: 'none' }}
            endIcon={collapsed ? <ExpandMoreIcon sx={{ fontSize: 14 }} /> : <ExpandLessIcon sx={{ fontSize: 14 }} />}
          >
            {collapsed ? 'Show' : 'Hide'}
          </Button>
        )}
      </Box>

      <ReviewProgressBar progress={progress} />

      {!collapsed && suggestions.length > 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
            {counts.critical > 0 && (
              <Chip label={`${counts.critical} critical`} size="small" color="error" variant="outlined"
                sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.6 } }} />
            )}
            {counts.warning > 0 && (
              <Chip label={`${counts.warning} warning`} size="small" color="warning" variant="outlined"
                sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.6 } }} />
            )}
            {counts.info > 0 && (
              <Chip label={`${counts.info} info`} size="small" color="info" variant="outlined"
                sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.6 } }} />
            )}
            {counts.accepted > 0 && (
              <Chip label={`${counts.accepted} accepted`} size="small" color="success"
                sx={{ height: 20, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.6 } }} />
            )}
            {counts.rejected > 0 && (
              <Chip label={`${counts.rejected} rejected`} size="small"
                sx={{ height: 20, bgcolor: theme.palette.action.disabledBackground, '& .MuiChip-label': { fontSize: '0.65rem', px: 0.6 } }} />
            )}
          </Box>

          {counts.applied > 0 && !isLocked && (
            <Box sx={{ display: 'flex', gap: 0.75, mb: 1 }}>
              <Button
                size="small"
                variant="contained"
                onClick={acceptAll}
                startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 26, fontSize: '0.72rem', textTransform: 'none',
                  bgcolor: theme.palette.success.main,
                  '&:hover': { bgcolor: theme.palette.success.dark },
                }}
              >
                Accept all {counts.applied}
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={rejectAll}
                startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{
                  height: 26, fontSize: '0.72rem', textTransform: 'none',
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.secondary,
                }}
              >
                Reject all
              </Button>
            </Box>
          )}

          <Box>
            {sortedSuggestions.map((s) => (
              <SuggestionCard
                key={s.suggestion_id}
                suggestion={s}
                onAccept={acceptOne}
                onReject={rejectOne}
                onScroll={scrollToOne}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export default DocumentReviewSuggestions;
